import { baseApi, TAGS } from 'shared/api';
import { BaseResponse } from 'shared/models/common';
import buildFilesFormData from 'shared/utils/buildFilesFormData';

import type {
  CreateFormOptions,
  CreateSpaceRenovationRequestPayload,
  UserSpaceRenovationRequest,
} from './types';

export const userSpaceRenovationRequestsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getUserSpaceRenovationRequestsList: build.query<
      UserSpaceRenovationRequest[],
      void
    >({
      query: () => ({
        url: '/user/cases/repair',
        method: 'GET',
      }),
      providesTags: [TAGS.USER_SPACE_RENOVATION_REQUESTS],
      transformResponse: (
        response: BaseResponse<UserSpaceRenovationRequest[]>
      ) => response.Data,
    }),
    getUserSpaceRenovationRequest: build.query<
      UserSpaceRenovationRequest,
      string
    >({
      query: (caseId) => ({
        url: `/user/cases/repair/${caseId}`,
        method: 'GET',
      }),
      serializeQueryArgs: ({ endpointName }) => endpointName,
      transformResponse: (response: BaseResponse<UserSpaceRenovationRequest>) =>
        response.Data,
    }),
    getUserSpaceRenovationRequestCreateFormOptions: build.query<
      CreateFormOptions,
      string
    >({
      query: (coopId) => ({
        url: `/user/cases/repair/form/create?CooperativeId=${coopId}`,
        method: 'GET',
      }),
      transformResponse: (response: BaseResponse<CreateFormOptions>) =>
        response.Data,
    }),
    createSpaceRenovationRequest: build.mutation<
      UserSpaceRenovationRequest,
      {
        body: CreateSpaceRenovationRequestPayload;
        cooperativeId: string;
        files: File[];
      }
    >({
      queryFn: async (
        { body, cooperativeId, files },
        _queryApi,
        _extra,
        fetchWithBQ
      ) => {
        const createSpaceRenovationRequestResult = await fetchWithBQ({
          url: '/user/cases/repair',
          method: 'POST',
          body,
        });

        if (createSpaceRenovationRequestResult.error) {
          return { error: createSpaceRenovationRequestResult.error };
        }

        const { Data: spaceRenovationRequest } =
          createSpaceRenovationRequestResult.data as BaseResponse<UserSpaceRenovationRequest>;

        if (files.length) {
          const uploadSpaceRenovationRequestFilesResult = await fetchWithBQ({
            url: `/user/cooperatives/${cooperativeId}/documents/incident/${spaceRenovationRequest.Id}`,
            method: 'POST',
            body: buildFilesFormData(files),
          });

          if (uploadSpaceRenovationRequestFilesResult.error) {
            const { Message } = uploadSpaceRenovationRequestFilesResult.error
              .data as { Message: string };
            const warning = `Selected files were not uploaded: ${Message}`;
            console.warn(warning);
          }
        }

        return {
          data: spaceRenovationRequest,
        };
      },
      invalidatesTags: (_, error) =>
        error ? [] : [TAGS.USER_SPACE_RENOVATION_REQUESTS],
    }),
  }),
});
